<template>
  <div>
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="fixed inset-0 flex z-40 md:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600/75" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="shrink-0 flex items-center px-4">
              <img class="h-8 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
            </div>
            <div class="px-4 mt-4">
              <QuickSearchButton
                @click="
                  quickSearchOpen = true;
                  sidebarOpen = false;
                "
              />
            </div>
            <div class="mt-3 flex-1 h-0 overflow-y-auto">
              <BoMenu @close="sidebarOpen = false" />
            </div>
          </div>
        </TransitionChild>
        <div class="shrink-0 w-14" aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden md:flex md:w-64 z-20 md:flex-col md:fixed md:inset-y-0">
      <div class="flex flex-col grow pt-5 overflow-hidden bg-white border-r border-gray-200">
        <div class="flex items-center shrink-0 px-4">
          <img class="h-6 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
        </div>
        <div class="shrink-0 px-4 mt-4">
          <QuickSearchButton @click="quickSearchOpen = true" />
        </div>
        <div class="mt-3 flex-1 overflow-y-auto">
          <BoMenu />
        </div>
        <div class="shrink-0 border-t border-gray-200">
          <Menu as="div" class="relative">
            <MenuButton
              class="w-full min-w-0 flex items-center px-4 py-3 text-left hover:bg-gray-100 group overflow-hidden"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">{{ currentUser.email }}</p>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 ml-2" />
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="z-50 absolute bottom-full left-0 mb-1 w-full rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden"
              >
                <MenuItem v-slot="{ active }">
                  <span
                    @click="signout"
                    :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 cursor-pointer']"
                  >
                    Sign out
                  </span>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
    <div class="md:pl-64 flex flex-col flex-1">
      <div class="md:hidden sticky top-0 z-30 shrink-0 flex h-16 bg-white shadow-sm">
        <button
          type="button"
          class="px-4 border-r border-gray-200 text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary md:hidden"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
        </button>
        <div class="flex-1 px-4 flex justify-between">
          <div class="flex-1 flex"></div>
          <div class="ml-4 flex items-center md:ml-6">
            <div class="p-2">
              <a
                class="flex items-center justify-center cursor-pointer"
                target="_blank"
                href="https://estaly.notion.site/FAQ-54e8759929b34ae6a87d05f93abc631e"
              >
                <QuestionMarkCircleIcon class="h-5 w-5 text-gray-500" />
                <p class="ml-1 text-sm text-gray-500">Help</p>
              </a>
            </div>
            <Menu as="div" class="ml-3 relative">
              <div>
                <MenuButton class="max-w-xs bg-white flex items-center text-sm rounded-full">
                  <span class="sr-only">Open user menu</span>
                  <div
                    class="h-10 w-10 rounded-full bg-primary-lighter hover:bg-primary flex items-center justify-center"
                  >
                    <p class="text-white">{{ currentUser.initials }}</p>
                  </div>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="z-50 origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden"
                >
                  <MenuItem v-slot="{ active }">
                    <span
                      @click="signout"
                      :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 cursor-pointer']"
                    >
                      Sign out
                    </span>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <div class="relative min-h-screen">
        <loading :active="isLoading" :is-full-page="false" :lock-scroll="true" :opacity="0.7" :color="'#4f46e5'" />
        <main>
          <div class="py-6">
            <div class="px-4">
              <router-view :key="$route.path"></router-view>
            </div>
          </div>
        </main>
      </div>
    </div>
    <QuickSearchDialog v-model="quickSearchOpen" />
  </div>
</template>

<script>
import { ref } from "vue";
import {
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import { ChevronDownIcon, MenuAlt2Icon, XIcon, QuestionMarkCircleIcon } from "@heroicons/vue/outline";

import { mapActions, mapState } from "vuex";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import BoMenu from "@/components/BoMenu.vue";
import QuickSearchButton from "@/components/quickSearch/QuickSearchButton.vue";
import QuickSearchDialog from "@/components/quickSearch/QuickSearchDialog.vue";
import { useCommandShortcut } from "@/composables/useCommandShortcut";

export default {
  components: {
    BoMenu,
    Dialog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    ChevronDownIcon,
    MenuAlt2Icon,
    XIcon,
    Loading,
    QuestionMarkCircleIcon,
    QuickSearchButton,
    QuickSearchDialog,
  },
  setup() {
    const quickSearchOpen = ref(false);
    useCommandShortcut(() => {
      quickSearchOpen.value = !quickSearchOpen.value;
    });
    return { quickSearchOpen };
  },
  data: () => {
    return {
      sidebarOpen: false,
    };
  },
  computed: {
    ...mapState("backoffice", ["isLoading"]),
    ...mapState("auth", ["currentUser"]),
  },
  methods: {
    ...mapActions("auth", ["signout"]),
  },
};
</script>
